import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { buildFilingUpload } from './filing-upload';
import type { FilingJobEvent, IXbrlEngine } from './xbrl-engine.interface';
import type { JobProgress, ReportsResult, ValidateResult } from './xbrl-engine.types';
import {
  asWorkerJobMessage,
  isReportsError,
  isReportsProgress,
  isReportsResult,
  isValidateError,
  isValidateProgress,
  isValidateResult,
  type WorkerJobMessage,
} from './xbrl-worker-messages';
import { XbrlWorkerBridgeService } from './xbrl-worker-bridge.service';

function newJobId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

type WorkerFilingRequest = {
  type: 'validate-request' | 'reports-request';
  id: string;
  filename: string;
  filingKind: 'xml' | 'zip' | 'csv-package';
  fileBytes?: ArrayBuffer;
  packageFiles?: Array<{ name: string; fileBytes: ArrayBuffer }>;
};

@Injectable({ providedIn: 'root' })
export class PyodideArelleEngine implements IXbrlEngine {
  private readonly bridge = inject(XbrlWorkerBridgeService);
  private engineReady = false;

  constructor() {
    this.bridge.messages$.subscribe((data) => {
      const record = data as Record<string, unknown>;
      if (record['type'] === 'taxonomy-loaded') {
        this.engineReady = true;
      }
    });
  }

  get ready(): boolean {
    return this.engineReady && this.bridge.supported;
  }

  validate(files: File[]): Observable<FilingJobEvent<ValidateResult>> {
    return this.runFilingJob(files, 'validate-request', (msg, id, displayName) => {
      if (isValidateProgress(msg, id)) {
        return {
          kind: 'progress',
          data: { phase: msg.phase, elapsedMs: msg.elapsedMs },
        };
      }
      if (isValidateResult(msg, id)) {
        const result: ValidateResult = {
          filename: displayName,
          counts: msg.counts,
          messages: msg.messages,
          truncatedAt: msg.truncatedAt,
          totalMs: msg.totalMs,
          validateFailed: msg.validateFailed,
        };
        return { kind: 'result', data: result };
      }
      if (isValidateError(msg, id)) {
        throw new Error(`${msg.phase}: ${msg.error}`);
      }
      return null;
    });
  }

  exportReports(files: File[]): Observable<FilingJobEvent<ReportsResult>> {
    return this.runFilingJob(files, 'reports-request', (msg, id, displayName) => {
      if (isReportsProgress(msg, id)) {
        return {
          kind: 'progress',
          data: { phase: msg.phase, elapsedMs: msg.elapsedMs },
        };
      }
      if (isReportsResult(msg, id)) {
        const result: ReportsResult = {
          downloadFilename: msg.downloadFilename,
          xlsxBytes: msg.xlsxBytes,
          debugRows: msg.debugRows ?? [],
          totalMs: msg.totalMs,
          tableCount: msg.tableCount,
        };
        return { kind: 'result', data: result };
      }
      if (isReportsError(msg, id)) {
        throw new Error(`${msg.phase}: ${msg.error}`);
      }
      return null;
    });
  }

  private runFilingJob<T>(
    files: File[],
    requestType: 'validate-request' | 'reports-request',
    mapMessage: (
      msg: WorkerJobMessage,
      id: string,
      displayName: string,
    ) => FilingJobEvent<T> | null,
  ): Observable<FilingJobEvent<T>> {
    return new Observable<FilingJobEvent<T>>((subscriber) => {
      if (!this.bridge.supported) {
        subscriber.error(new Error('Web Workers are not supported in this browser'));
        return;
      }

      const id = newJobId();
      let displayName = files.map((f) => f.name).join(', ');
      const sub = this.bridge.messages$.subscribe((data) => {
        const msg = asWorkerJobMessage(data);
        if (!msg) return;
        try {
          const mapped = mapMessage(msg, id, displayName);
          if (mapped !== null) {
            subscriber.next(mapped);
            if (mapped.kind === 'result') {
              subscriber.complete();
            }
          }
        } catch (err) {
          subscriber.error(err);
        }
      });

      void buildFilingUpload(files)
        .then((payload) => {
          displayName = payload.displayName;
          const transfers: Transferable[] = [];
          const message: WorkerFilingRequest = {
            type: requestType,
            id,
            filename: payload.displayName,
            filingKind: payload.kind,
          };
          if (payload.fileBytes) {
            message.fileBytes = payload.fileBytes;
            transfers.push(payload.fileBytes);
          }
          if (payload.packageFiles?.length) {
            message.packageFiles = payload.packageFiles.map((f) => {
              transfers.push(f.fileBytes);
              return { name: f.name, fileBytes: f.fileBytes };
            });
          }
          this.bridge.post(message, transfers);
        })
        .catch((err: unknown) => {
          subscriber.error(err instanceof Error ? err : new Error(String(err)));
        });

      return () => sub.unsubscribe();
    });
  }
}
